import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { ModalService } from '@app/core/services/modal.service';
import { parseNumber } from '@app/core/services/utility.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { CustomerService } from '@app/services/customer.service';
import { DiscountGroupProductGet, DiscountGroupProductItem, DiscountGroupService } from '@app/services/discount-group.service';
import { EconomyService, ExternalPriceMethodItem, PriceListItem } from '@app/services/economy.service';
import { BusinesscoItem, LogisticService, PlanPolicyItem, PohCompletionMethodItem, PohCompletionPrincipleItem, ProdBeoMethodItem, ProductionPlaceItem, UnitItem } from '@app/services/logistic.service';
import { ProductGroup2Item, ProductGroup3Item, ProductGroup4Item, ProductGroupItem, ProductGroupService } from '@app/services/product-group.service';
import { ProductSettingGet, ProductSettingItem, ProductSettingMcItem, ProductSettingMcsInput, ProductSettingService } from '@app/services/product-setting.service';
import { ConversionUnitItem, GetProductCreateParams, ProductCreateGet, ProductCreateSalesPrice, ProductItem, ProductService, ProductTypeItem } from '@app/services/product.service';
import { CustomerItem, TypeaheadService } from '@app/services/typeahead.service';
import { catchError, debounceTime, EMPTY, from, Subject, switchMap, takeUntil } from 'rxjs';

@Component({
    selector: 'tt-product-create',
    templateUrl: './product-create.component.html',
    styleUrls: ['./product-create.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCreateComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public params?: GetProductCreateParams;

    public get?: ProductCreateGet & ProductCreateSalesPrice;

    @Output()
    public submit = new EventEmitter<void>();

    public productTypes: ProductTypeItem[] = [];

    public productSettings: ProductSettingItem[] = [];

    public productGroups: ProductGroupItem[] = [];

    public productGroup2s: ProductGroup2Item[] = [];

    public productGroup3s: ProductGroup3Item[] = [];

    public productGroup4s: ProductGroup4Item[] = [];

    public pimFields: ProductSettingMcsInput[] = [];

    public pimFieldsItems: { [key: string]: ProductSettingMcItem[] } = {};

    public units: UnitItem[] = [];

    public conversionUnits: ConversionUnitItem[] = [];

    public discountGroupsProducts: DiscountGroupProductItem[] = [];

    public priceMethods: ExternalPriceMethodItem[] = [];

    public pricelists: PriceListItem[] = [];

    public companies: BusinesscoItem[] = [];

    public pohCompletionMethods: PohCompletionMethodItem[] = [];

    public pohCompletionPrinciples: PohCompletionPrincipleItem[] = [];

    public productionOrderMethods: ProdBeoMethodItem[] = [];

    public planPolicies: PlanPolicyItem[] = [];

    public productionPlaces: ProductionPlaceItem[] = [];

    public loadSalesPrice$ = new Subject<ProductCreateGet>();

    public loadDiscountInfo$ = new Subject<ProductCreateGet>();

    public destroy$ = new Subject<void>();

    public saving = false;

    constructor(
        private product: ProductService,
        private productSetting: ProductSettingService,
        private productGroup: ProductGroupService,
        private logistic: LogisticService,
        private typeahead: TypeaheadService,
        private customer: CustomerService,
        private discountGroup: DiscountGroupService,
        private economy: EconomyService,
        private modal: ModalService,
        private cdr: ChangeDetectorRef,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
        this.loadSalesPrice$
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(500),
                switchMap((params) => from(this.product.calcProductCreateSalesPrice({ ...params, units: this.conversionUnits })).pipe(catchError((_) => EMPTY))),
            )
            .subscribe({
                next: (result) => {
                    if (!this.get) return;

                    for (let k in result) {
                        this.get[k as keyof ProductCreateGet] = result[k as keyof ProductCreateSalesPrice] as any;
                    }

                    this.markForChange();
                },
            });

        this.loadDiscountInfo$
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(150),
                switchMap((params) => from(this.discountGroup.getDiscountGroupProduct(params)).pipe(catchError((_) => EMPTY))),
            )
            .subscribe({
                next: (result) => {
                    if (!this.get) return;

                    for (let k in result) {
                        if (k in this.get) {
                            this.get[k as keyof ProductCreateGet] = result[k as keyof DiscountGroupProductGet] as any;
                        }
                    }

                    this.markForChange();
                },
            });
    }

    private markForChange() {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        setTimeout(() => {
            this.cdr.markForCheck();
            this.cdr.detectChanges();
        });
    }

    public async save() {
        if (!this.get || !this.params) return;

        try {
            this.saving = true;
            this.markForChange();
            const { errorcode, errormessage, item_state, item_parms } = await this.product.createProduct({ ...this.params, ...this.get, units: this.conversionUnits, metacolumns: this.pimFields.map((field) => ({ productsettingmc_keyno: field.productsettingmc_keyno, elementtype: field.elementtype, field_value: field.field_value, isrequired: field.isrequired })) });

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.emit();

                if (['dialog', 'popup'].includes(this.navigator?.host ?? '')) {
                    this.navigator?.back(undefined, { ...this.params, ...this.get, item_state, item_parms });
                } else {
                    if (!!item_state) {
                        this.navigator?.go({ to: item_state, parms: item_parms });
                    } else {
                        this.navigator?.back();
                    }
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
            this.markForChange();
        }
    }

    public recalculateSuppliersPrice() {
        if (!!this.get) {
            this.get.suppliers_price = parseNumber(this.get.fullprice_val) - parseNumber(this.get.fullprice_val) * (parseNumber(this.get.purch_discount) / 100);
            this.markForChange();
        }
    }

    public setConversionInversed(unit: ConversionUnitItem) {
        if (!this.get) return;
        unit.inversevalue = unit.inversevalue === '1' ? '0' : '1';
        unit.qtyfactor = 1 / parseNumber(unit.qtyfactor);

        this.loadSalesPrice$.next(this.get);
        this.markForChange();
    }

    public searchProducts = async () => {
        if (!this.get) {
            return [];
        } else {
            return this.product.getProducts({
                prod_name: this.get.product_search,
                webpage_name: 'productcreate',
                parm1: this.params!.argtype,
                parm2: this.params!.argvalue,
                supplier_no: '',
                businessco_no: this.get.businessco_no,
            });
        }
    };

    public searchSuppliers = async () => {
        if (!this.get) {
            return [];
        } else {
            return this.typeahead.searchCustomer({ cust_name: this.get.searchtext_supplier, custtype: 'S', lastselect: 'SEARCH' });
        }
    };

    public async onProductSelected(event?: ProductItem) {
        if (!this.get) return;

        this.get.prod_id_copy_from = event?.item_id ?? '';
        this.get.product_search = event?.item_name ?? '';

        this.markForChange();

        const copyFrom = await this.product.getProductToCopyFrom(this.get);

        this.get.productsetting_keyno = copyFrom?.productsetting_keyno ?? '0';

        if (this.get.productsetting_keyno > '0') {
            await this.loadProductSetting();
        }

        await this.loadPimData();

        this.get.prod_name = !copyFrom ? '' : this.get.prod_name || copyFrom.prod_name;
        this.get.prodtype_no = copyFrom?.prodtype_no ?? '';
        this.get.prodgrp_id = copyFrom?.prodgrp_id ?? '';
        this.get.prodgrp2_id = copyFrom?.prodgrp2_id ?? '';
        this.get.prodgrp3_id = copyFrom?.prodgrp3_id ?? '';
        this.get.prodgrp4_id = copyFrom?.prodgrp4_id ?? '';
        this.get.supplier_no = copyFrom?.supplier_no ?? '';
        this.get.searchtext_supplier = copyFrom?.supplier_name ?? '';
        this.get.suppliers_valuta = copyFrom?.suppliers_valuta ?? '';

        this.loadProductGroup2s();
        this.loadProductGroup3s();
        this.loadProductGroup4s();

        this.markForChange();
    }

    public async onSupplierSelected(event?: CustomerItem) {
        if (!this.get) return;

        this.get.supplier_no = event?.item_id ?? '0';
        this.get.searchtext_supplier = event?.item_name ?? '';
        this.markForChange();

        this.get.suppliers_valuta = (await this.customer.getCustomerEdit({ cust_no: this.get.supplier_no })).cust_invoice_valuta_id;

        this.markForChange();
    }

    public async onProductSettingChanged() {
        if (!this.get) return;

        this.loadPimData();

        if (this.get.productsetting_keyno > '0') await this.loadProductSetting();

        if (this.get.prodgrp_id > '') this.loadProductGroup2s();
        if (this.get.prodgrp2_id > '') this.loadProductGroup3s();
        if (this.get.prodgrp3_id > '') this.loadProductGroup4s();

        this.loadConversionUnits();
        this.loadSalesPrice$.next(this.get);
        this.loadDiscountInfo$.next(this.get);
        this.loadProductSettings();

        this.markForChange();
    }

    public async onProductGroupChanged() {
        if (!this.get) return;

        if (this.get.prodgrp_id > '') {
            await this.loadProductGroup2s();
        } else {
            this.productGroup2s = [];
        }

        this.get.prodgrp2_id = '';

        this.markForChange();
        this.onProductGroup2Changed();
    }

    public async onProductGroup2Changed() {
        if (!this.get) return;

        if (this.get.prodgrp2_id > '') {
            await this.loadProductGroup3s();
        } else {
            this.productGroup3s = [];
        }

        this.get.prodgrp3_id = '';
        this.markForChange();
        this.onProductGroup3Changed();
    }

    public async onProductGroup3Changed() {
        if (!this.get) return;

        if (this.get.prodgrp3_id > '') {
            await this.loadProductGroup4s();
        } else {
            this.productGroup4s = [];
        }

        this.get.prodgrp4_id = '';
        this.markForChange();
    }

    public async getProdId() {
        if (!this.get) return;

        const { nextnumber } = await this.product.getProductCreateProdId({ productsetting_keyno: this.get.productsetting_keyno });

        this.get.prod_id = nextnumber;
        this.get.new_number_assigned = '1';

        this.markForChange();
        await this.loadExistingProdIds();
        this.markForChange();
    }

    public onPimFieldsChange() {
        if (!this.get || this.get.prod_name_get > '') return;

        let newProductId = '';
        let newProductName = '';

        const shouldBuildProdId = this.pimFields.some((item) => item.build_prod_id === '1');
        const shouldBuildProdName = this.pimFields.some((item) => item.build_product_name === '1');

        if (shouldBuildProdId) this.get.prod_id = '';
        if (shouldBuildProdName) this.get.prod_name = '';

        this.pimFields.forEach((item, index) => {
            this.setNewQuantityFactor(index);

            let newFieldValue = item.elementtype === 'CL' ? (this.pimFieldsItems[item.productsettingmc_keyno]?.find((e) => e.productsettingmcitem_keyno === item.field_value)?.productsettingmcitem_name ?? '') : item.field_value;

            if (item.build_prod_id === '1') {
                newProductId = this.appendProdId(newProductId, newFieldValue, item);
            }

            if (item.build_product_name === '1') {
                newProductName = this.appendProdName(newProductName, newFieldValue, item);
            }
        });

        if (shouldBuildProdId) this.get.prod_id = newProductId.toUpperCase();
        if (shouldBuildProdName) this.get.prod_name = newProductName;

        this.markForChange();

        this.loadExistingProdIds();
    }

    private setNewQuantityFactor(index: number) {
        for (let unitItem of this.conversionUnits) {
            if (index === 0) {
                unitItem.conversion_formula_replaced = unitItem.conversion_formula;
            }

            unitItem.conversion_formula_replaced = unitItem.conversion_formula_replaced.replaceAll(new RegExp(`\\b${this.pimFields[index].field_name}\\b`, 'g'), this.pimFields[index].field_value);

            if (index === this.pimFields.length - 1 && /^[0-9+\-*/()\s]*$/.test(unitItem.conversion_formula_replaced)) {
                let newValue = new Function('return ' + unitItem.conversion_formula_replaced)();

                if (!isNaN(newValue)) {
                    unitItem.qtyfactor = newValue;
                }
            }
        }
    }

    private appendProdId(newProductId: string, newFieldValue: string, item: ProductSettingMcsInput) {
        if (newFieldValue?.length > 0) {
            if (newProductId.length > 0) newProductId += '';

            if (+item.prod_id_length > 0) {
                newProductId += item.prod_id_prefix + newFieldValue.slice(0, +item.prod_id_length) + item.prod_id_postfix;
            } else {
                if (item.elementtype === 'N1' && newFieldValue.indexOf('.0')) {
                    newFieldValue = newFieldValue.replace('.0', '');
                }

                if (item.elementtype === 'N2' && newFieldValue.indexOf('.00')) {
                    newFieldValue = newFieldValue.replace('.00', '');
                }

                if (item.elementtype.includes('N')) {
                    if (newFieldValue !== '0') {
                        newProductId += item.prod_id_prefix + newFieldValue + item.prod_id_postfix;
                    }
                } else {
                    newProductId += item.prod_id_prefix + newFieldValue + item.prod_id_postfix;
                }
            }
        }

        return newProductId;
    }

    private appendProdName(newProductName: string, newFieldValue: string, item: ProductSettingMcsInput) {
        if (newFieldValue.length > 0) {
            if (newProductName.length > 0) newProductName += ' ';
            newProductName += item.prefix_value + newFieldValue;
        }

        return newProductName;
    }

    private async loadProductTypes() {
        this.productTypes = await this.product.getProductTypes();
    }

    public async loadExistingProdIds() {
        if (!!this.get) {
            this.markForChange();
            this.get.existing_prod_ids = (await this.product.getProductCreateExisting({ prod_id: this.get.prod_id })).existing_prod_ids;
            this.markForChange();
        }
    }

    public async loadProductSettings() {
        if (!this.get) return;

        this.productSettings = await this.productSetting.getProductSettings({ prodtype_no: this.get.prodtype_no });

        if (!this.productSettings.some((item) => item.item_id === this.get!.productsetting_keyno)) {
            this.get.productsetting_keyno = this.productSettings[0]?.item_id ?? '0';
            this.onProductSettingChanged();
        }
    }

    public async loadProductSetting() {
        if (!this.get) return;

        const setting = await this.productSetting.getProductSetting({ productsetting_keyno: this.get.productsetting_keyno });

        for (let k in setting) {
            let key: keyof Omit<ProductSettingGet, 'productsetting_name' | 'unique_counter_autoproductean'> = k as keyof Omit<ProductSettingGet, 'productsetting_name' | 'unique_counter_autoproductean'>;

            if (key in this.get) {
                this.get[key] = setting[key] as any;
            }
        }
    }

    private async loadProductGroups() {
        this.productGroups = await this.productGroup.getProductGroups();
    }

    private async loadProductGroup2s() {
        if (!!this.get) {
            this.productGroup2s = await this.productGroup.getProductGroup2s({ prodgrp_id: this.get.prodgrp_id });
            this.markForChange();
        }
    }

    private async loadProductGroup3s() {
        if (!!this.get) {
            this.productGroup3s = await this.productGroup.getProductGroup3s({ prodgrp2_id: this.get.prodgrp2_id });
            this.markForChange();
        }
    }

    private async loadProductGroup4s() {
        if (!!this.get) {
            this.productGroup4s = await this.productGroup.getProductGroup4s({ prodgrp3_id: this.get.prodgrp3_id });
            this.markForChange();
        }
    }

    private async loadPimFields() {
        if (!!this.get) {
            this.pimFields = await this.productSetting.getProductCreatePimFields({ productsetting_keyno: this.get.productsetting_keyno });
        }
    }

    private async loadPimFieldsItems() {
        if (!!this.get) {
            this.pimFieldsItems = await this.productSetting.getPimFieldsItems({ productsetting_keyno: this.get.productsetting_keyno });
        }
    }

    private async loadUnits() {
        this.units = await this.logistic.getUnitList({ isactive: '1' });
    }

    private async loadDiscountGroupsProducts() {
        this.discountGroupsProducts = await this.discountGroup.getDiscountGroupProducts();
    }

    private async loadPriceMethods() {
        this.priceMethods = await this.economy.getExternalPriceMethods();
    }

    private async loadPricelists() {
        this.pricelists = await this.economy.getPriceLists();
    }

    public async loadConversionUnits() {
        if (!!this.get) {
            if (this.get.show_unitconversions === '1') {
                this.conversionUnits = await this.product.getProductCreateConversionUnits(this.get);
            } else {
                this.conversionUnits = [];
            }
        }
    }

    private async loadCompanies() {
        this.companies = await this.logistic.getCompanies();
    }

    private async loadCompletionMethods() {
        this.pohCompletionMethods = await this.logistic.getPohCompletionMethods();
    }

    private async loadCompletionPrinciples() {
        this.pohCompletionPrinciples = await this.logistic.getPohCompletionPrinciples();
    }

    private async loadProductionOrderMethods() {
        this.productionOrderMethods = await this.logistic.getProdBeoMethods();
    }

    private async loadPlanPolicies() {
        this.planPolicies = await this.logistic.getPlanPolicies();
    }

    private async loadProductionPlaces() {
        this.productionPlaces = await this.logistic.getProductionPlaces();
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = (await this.product.getProductCreate(this.params)) as any;
        }
    }

    private async loadPimData() {
        await Promise.all([this.loadPimFields(), this.loadPimFieldsItems()]);
        this.onPimFieldsChange();
    }

    private async init() {
        this.loadProductTypes();
        this.loadUnits();
        this.loadProductGroups();
        this.loadDiscountGroupsProducts();
        this.loadPriceMethods();
        this.loadPricelists();
        this.loadCompanies();
        this.loadCompletionMethods();
        this.loadCompletionPrinciples();
        this.loadProductionOrderMethods();
        this.loadPlanPolicies();
        this.loadProductionPlaces();
        await this.loadGet();
        await this.loadProductSetting();
        this.markForChange();
        this.loadProductSettings();
        this.loadPimData();
        this.loadConversionUnits();

        if (!!this.get) {
            this.loadSalesPrice$.next(this.get);
            this.loadDiscountInfo$.next(this.get);
        }

        this.loadProductGroup2s();
        this.loadProductGroup3s();
        this.loadProductGroup4s();
    }

    ngOnInit(): void {
        if (!!this.navigator) {
            this.navigator.setSize?.({ width: 'min(95vw, 140rem)' });

            if (['dialog', 'popup'].includes(this.navigator.host)) {
                this.navigator.setHeading?.('p2_m_productcreate', true);
            }

            this.params = this.navigator.params as any;
            this.init();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.loadSalesPrice$.complete();
        this.loadDiscountInfo$.complete();
    }
}
